import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Trash2, Loader2 } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { favoritesAPI } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";

export default function Favorites() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  const fetchFavorites = async () => {
    try {
      const res = await favoritesAPI.list();
      setFavorites(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const removeFavorite = async (id) => {
    setRemovingId(id);
    try {
      await favoritesAPI.remove(id);
      setFavorites(favorites.filter((f) => f.id !== id));
    } catch {
      // silent
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="min-h-screen max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="favorites-page">
      <div className="flex items-center gap-3 mb-8">
        <Heart className="w-6 h-6 text-red-500" />
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-white">
          {t("favoritesTitle")}
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : favorites.length === 0 ? (
        <div className="text-center py-20">
          <Heart className="w-12 h-12 text-stone-700 mx-auto mb-4" />
          <p className="text-stone-500">{t("noFavorites")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {favorites.map((fav) => (
            <div key={fav.id} className="relative group">
              <RecipeCard
                recipe={fav.recipe || fav}
                onClick={() => navigate("/recipe", { state: { recipe: fav.recipe || fav } })}
              />
              <button
                onClick={(e) => { e.stopPropagation(); removeFavorite(fav.id); }}
                disabled={removingId === fav.id}
                data-testid={`remove-fav-${fav.id}`}
                className="absolute top-3 right-3 p-2 bg-black/60 backdrop-blur-sm rounded-full text-red-400 hover:text-red-300 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all z-10"
              >
                {removingId === fav.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
